import { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { ToastContainer, Zoom } from 'react-toastify';
import 'react-quill-new/dist/quill.snow.css';
import 'react-date-range/dist/styles.css';
import 'slick-carousel/slick/slick.css';
import { configure } from 'mobx';
import { logger } from '@cutwise/utils';

import { enableOpenreplay } from '@/common/helpers/enableOpenreplay';
import DebugModeService from '@/common/services/DebugModeService';
import Notifier from '@/common/services/Notifier';
import { configurationStore } from '@/common/stores/ConfigurationStore';
import config from '@/config';
import { netBoxStore } from '@/netbox/stores/NetBoxStore';
import { StoreProvider } from '@/store';

import '@/styles/index.css';

import Root from './Root';

const BrowserSupport = lazy(() => import('@/common/components/BrowserSupport/BrowserSupport'));
const DebugPanel = lazy(() => import('@/debug/components/DebugPanel/DebugPanel'));

configure({ isolateGlobalState: true, enforceActions: 'observed' });

logger.configure({ prefix: 'CUTWISE' });
logger.info('Version', config.APP_VERSION);

DebugModeService.enableDebugMode();

netBoxStore.connectToNetBox();

// load constants as soon as possible, before App starts render
configurationStore.fetchServerConstants();

enableOpenreplay();

createRoot(document.getElementById('browser-support-container')!).render(
  <Suspense fallback={null}>
    <BrowserSupport />
  </Suspense>,
);

createRoot(document.getElementById('notifications-container')!).render(
  <ToastContainer hideProgressBar limit={4} transition={Zoom} />,
);

createRoot(document.getElementById('app')!).render(
  <StoreProvider>
    {DebugModeService.isEnabled && (
      <Suspense fallback={null}>
        <DebugPanel />
      </Suspense>
    )}

    <Suspense fallback={null}>
      <Root />
    </Suspense>
  </StoreProvider>,
);

window.addEventListener('offline', () => {
  Notifier.warn('Internet connection is lost');
});

/*
 * Handle "UnhandledRejection" global event
 * https://developer.mozilla.org/en-US/docs/Web/API/Window/unhandledrejection_event#basic_error_logging
 * */
window.addEventListener('unhandledrejection', (e) => {
  logger.error('Unhandled rejection (promise: ', e.promise, ', reason: ', e.reason, ').');

  e.preventDefault();
  e.stopPropagation();
});

if ('serviceWorker' in navigator && SW_URL) {
  navigator.serviceWorker
    .register(SW_URL, { type: SW_TYPE })
    .then((reg) => logger.info(`SW registration succeeded. Scope is ${reg.scope}`))
    .catch((err) => logger.error(`SW registration failed with error ${err}`));
}
